import {getScoreCssColor, getScoreRanking} from './creditScoreUtils';

import {LogDataObj} from './creditHelper';
import _isEmpty from 'lodash/isEmpty';
import _isNull from 'lodash/isNull';
import _isUndefined from 'lodash/isUndefined';
import _sortBy from 'lodash/sortBy';
import {currencyCodes} from '../constants/currencyCodeMap';
import dateUtils from './dateUtils';
import utils from './utils';

export function buildCreditReportData(
  data: GetCreditReportAndInsightsResponse,
  localizedStringBundle: StringMap,
  langCode: string,
  provider: string,
  accountId: string,
  countryCode: string
): CreditReportProvider | null {
  const startTime = Date.now();
  const clientId = window.isDwm ? 'dwm' : 'dsp';
  const logObj: LogDataObj = {
    feature: 'Credit',
    actionType: 'Building Credit Report Data',
    startTime,
    clientId,
    statusCode: 204,
    accountId,
  };

  if (!data || _isEmpty(data) || typeof data === 'string') {
    logObj.message = `Data is either null|empty|string=>${typeof data}`;
    utils.log(logObj);
    return null;
  }
  const reportAndInsights: GetCreditReportAndInsightsResponse['reportAndInsights'] = utils.safeGet(
    data,
    'reportAndInsights',
    []
  );
  if (_isEmpty(reportAndInsights)) {
    logObj.message = `reportAndInsights is either null|empty=>${typeof reportAndInsights}`;
    utils.log(logObj);
    return null;
  }
  const providerViewFinal: ProviderViewFinalType[] = [];

  // adding a defensive check incase there is different data type returned for reportAndInsights as filter() and forEach() are array specific functions
  if (Array.isArray(reportAndInsights)) {
    const generatedDate = reportAndInsights[0]?.report?.generatedDate || '';

    const latest = reportAndInsights.filter((item) => {
      return item.report.provider === provider;
    });

    // Preparing score parameters for the ArcChart
    // Don't translate the names because they are used as lookup values
    const defaultScoreRanges: ScoreRange[] = [
      {low: 300, high: 599, name: 'Poor'},
      {low: 600, high: 660, name: 'Fair'},
      {low: 661, high: 715, name: 'Good'},
      {low: 716, high: 747, name: 'Very Good'},
      {low: 748, high: 850, name: 'Excellent'},
    ];

    reportAndInsights.forEach((item, index) => {
      const {report, insights} = item;
      // Convert the unified model to the old provider view structure
      const convertedProviderView: ProviderViewFinalType = {
        provider: report.provider,
        summary: convertedSummaryFromReport(item, countryCode),
        revolvingAccounts: convertToCreditAccounts(countryCode, report.accounts.revolving, report.provider),
        mortgageAccounts: convertToCreditAccounts(countryCode, report.accounts.mortgage, report.provider),
        installmentAccounts: convertToCreditAccounts(countryCode, report.accounts.instalment, report.provider),
        otherAccounts: convertToCreditAccounts(countryCode, report.accounts.otherAccounts, report.provider),
        inquiries: null, // Will be filled below
        consumerStatements: [], // Not available in unified model
        publicRecords: convertToPublicRecords1(report.publicRecords, report.provider),
        collections: convertToCollections(countryCode, report.collections, report.provider),
        creditFreezeStatus: report.creditFreezeStatus || {
          freezeDate: '',
          freezeStatusInquiryDate: '',
          isFreezeSupported: false,
          isFrozen: false,
        },
        insights,
        bankingRecords: convertToCreditAccounts(countryCode, report.accounts.bankingRecords, report.provider),
        tuCollections: convertToCreditAccounts(countryCode, report.accounts.collections, report.provider),
        openAccounts: convertToCreditAccounts(countryCode, report.accounts.open, report.provider),
      };
      providerViewFinal[index] = convertedProviderView;

      const inquiries =
        !_isNull(report.inquiries) && !_isUndefined(report.inquiries)
          ? _sortBy(
              report.inquiries.map((inquiry) => ({
                provider: report.provider,
                id: inquiry.id || null,
                type: inquiry.type as 'HARD' | 'SOFT',
                prefix: null,
                reportedDate: safeNumberConvert(inquiry.reportedDate),
                contactInformation: {
                  contactName: inquiry.inquirerInformation?.name || '',
                  address: convertToAddress1(inquiry.inquirerInformation?.address),
                  phone: inquiry.inquirerInformation?.phone || null,
                },
              })),
              (inquire) => {
                return 0 - inquire.reportedDate;
              }
            )
          : [];

      const creditFreezeStatus = report.creditFreezeStatus || {};
      let percentage = null;
      const date = utils.safeGet(report, 'generatedDate', localizedStringBundle.CREDIT_SCORE_DATE_UNAVAILABLE);
      const formattedDate = dateUtils.convertDateToSlashFormat(date, langCode, '-');
      let score = report.creditScore || 'NA';
      const scoreType = '';
      const scoreRanges = defaultScoreRanges;
      const scoreReasons: ScoreReason1[] = []; // available in Credit Score Model
      let scoreName = null;

      const isScoreValid = !(score === 0 || score === 1 || score === 4 || score === 'NA');

      // Calculate scoreName
      if (isScoreValid) {
        const range =
          scoreRanges && scoreRanges.length > 0
            ? scoreRanges.filter((latestRange) => {
                if ((score as number) >= latestRange.low && (score as number) <= latestRange.high) {
                  return latestRange.name;
                }
                return '';
              })
            : '';
        scoreName = utils.safeGet(range, '[0].name', '');
      } else {
        scoreName = localizedStringBundle.CREDIT_SCORE_NA;
      }

      const scoreRangeIndex: number = getScoreRanking(scoreName);
      const cssColor: string = getScoreCssColor(scoreName);

      // Calculate percentage
      if (isScoreValid) {
        const lowScore = utils.safeGet(scoreRanges[scoreRangeIndex], 'low', 0);
        const highScore = utils.safeGet(scoreRanges[scoreRangeIndex], 'high', 100);
        const perc = scoreRangeIndex * 20 + (((score as number) - lowScore) / (highScore - lowScore)) * 20;
        percentage = perc.toFixed(0);
      } else {
        percentage = 0;
        if (score === 'NA') {
          score = localizedStringBundle.CREDIT_SCORE_DASH;
        }
      }

      providerViewFinal[index].inquiries = inquiries;
      providerViewFinal[index].creditFreezeStatus = creditFreezeStatus;
      providerViewFinal[index].summary.creditScore = {
        cssColor,
        date,
        formattedDate,
        percentage,
        score,
        scoreName: scoreName as string,
        scoreRanges,
        scoreRangeIndex,
        scoreType,
        scoreReasons,
      };
    });

    const id = latest[0]?.report?.reportId || null;

    // Summary
    const summary =
      latest && latest[0] && latest[0].report
        ? convertedSummaryFromReport(latest[0], countryCode)
        : localizedStringBundle.CREDIT_SCORE_UNAVAILABLE;
    const defaultCurrency = getCurrency('', countryCode);

    // Summary - Revolving Accounts
    const revolvingAccounts: AccountSummary1 | {} =
      (summary as ProviderViewFinalType['summary']).revolvingAccounts || {};

    // Revolving Accounts - Available
    const revolvingAvailable: Money1 | {} = (revolvingAccounts as AccountSummary1).available
      ? (revolvingAccounts as AccountSummary1).available
      : {};
    const revolvingAvailableAmt = (revolvingAvailable as Money1).amount
      ? (revolvingAvailable as Money1).amount
      : `${defaultCurrency} 0`;

    // Summary - Revolving Accounts - Balance
    const revolvingBalance: Money1 | {} = (revolvingAccounts as AccountSummary1).balance
      ? (revolvingAccounts as AccountSummary1).balance
      : {};
    const revolvingBalanceAmt = (revolvingBalance as Money1).amount
      ? (revolvingBalance as Money1).amount
      : `${defaultCurrency} 0`;
    const revolvingBalanceCurrency = (revolvingBalance as Money1).currency
      ? (revolvingBalance as Money1).currency
      : '$';

    // Summary - Revolving Accounts - Credit Limit
    const revolvingCreditLimit: Money1 | {} = (revolvingAccounts as AccountSummary1).creditLimit
      ? (revolvingAccounts as AccountSummary1).creditLimit
      : {};
    const revolvingCreditLimitAmt = (revolvingCreditLimit as Money1).amount
      ? (revolvingCreditLimit as Money1).amount
      : `${defaultCurrency} 0`;

    // Summary - Revolving Accounts - Debt To Credit Ratio
    const revolvingDtcRatio = (revolvingAccounts as AccountSummary1).debtToCreditRatio
      ? (revolvingAccounts as AccountSummary1).debtToCreditRatio
      : localizedStringBundle.CREDIT_SCORE_NOT_AVAILABLE;

    // Summary - Revolving Accounts - Monthly Payment
    const revolvingMonthlyPayment = (revolvingAccounts as AccountSummary1).monthlyPaymentAmount
      ? (revolvingAccounts as AccountSummary1).monthlyPaymentAmount
      : {};
    const revolvingMonthlyPaymentAmount = (revolvingMonthlyPayment as Money1).amount
      ? (revolvingMonthlyPayment as Money1).amount
      : `${defaultCurrency} 0`;

    // Summary - Revolving Accounts - Total Count
    const revolvingTotal = (revolvingAccounts as AccountSummary1).totalAccounts
      ? (revolvingAccounts as AccountSummary1).totalAccounts
      : '0';
    const revolvingTotalWithBalance = (revolvingAccounts as AccountSummary1).totalAccountsWithBalance
      ? (revolvingAccounts as AccountSummary1).totalAccountsWithBalance
      : '0';

    // Summary - Accounts
    const averageAccountAgeMonths = (summary as ProviderViewFinalType['summary']).averageAccountAgeMonths
      ? (summary as ProviderViewFinalType['summary']).averageAccountAgeMonths
      : localizedStringBundle.CREDIT_SCORE_NOT_AVAILABLE;
    const creditFileSecurityFreezeFlag = (summary as ProviderViewFinalType['summary']).creditFileSecurityFreezeFlag
      ? (summary as ProviderViewFinalType['summary']).creditFileSecurityFreezeFlag
      : localizedStringBundle.CREDIT_SCORE_NOT_AVAILABLE;

    // Score
    const creditScore =
      latest && latest[0] && providerViewFinal.find((p) => p.provider === provider)?.summary.creditScore
        ? providerViewFinal.find((p) => p.provider === provider)?.summary.creditScore
        : localizedStringBundle.CREDIT_SCORE_UNAVAILABLE;
    const score =
      latest && latest[0] && creditScore
        ? (creditScore as ProviderViewFinalType['summary']['creditScore']).score
        : localizedStringBundle.CREDIT_SCORE_UNAVAILABLE;
    const scorePercentage = (scorePerc: number) => {
      return (550 / scorePerc) * 100;
    };

    // Date
    let formattedDate = '';
    const date = utils.safeGet(summary, 'reportGenerated', localizedStringBundle.CREDIT_SCORE_NA);
    if (date !== 'NA') {
      formattedDate = dateUtils.convertDateToSlashFormat(date, langCode, '-');
    } else {
      formattedDate = localizedStringBundle.CREDIT_SCORE_DATE_UNAVAILABLE;
    }

    const translateReportType = (type: string) => {
      let translatedType = null;
      switch (type) {
        case 'US_3B':
          translatedType = localizedStringBundle['3_BUREAU_CREDIT_REPORT'];
          break;
        case 'US_EFX':
          translatedType = localizedStringBundle.EQUIFAX_REPORT;
          break;
        default:
          translatedType = '';
      }
      return translatedType;
    };

    // Report Type
    const reportType = latest[0]?.report?.reportType || localizedStringBundle.CREDIT_SCORE_REPORT_UNAVAILABLE;
    const translatedType = translateReportType(reportType);

    // Score Reasons - not available in unified report model - default value is []
    const reasons =
      summary && creditScore && score
        ? (creditScore as ProviderViewFinalType['summary']['creditScore']).scoreReasons
        : localizedStringBundle.CREDIT_SCORE_NOT_AVAILABLE;

    // Loan Risk Ranges - not available in unified model
    const loanRiskRanges = localizedStringBundle.CREDIT_SCORE_NOT_AVAILABLE;

    // Score Ranges
    const percentage = scorePercentage(score as number);
    const ranges =
      summary && creditScore && score
        ? (creditScore as ProviderViewFinalType['summary']['creditScore']).scoreRanges
        : [];
    const ranking =
      ranges && ranges.length > 0
        ? ranges.map((latestRange) => {
            if ((score as number) > latestRange.low && (score as number) < latestRange.high) {
              return latestRange.name;
            }
            return null;
          })
        : '';

    const report = {
      generatedDate,
      activeReport: formattedDate.concat(' ').concat('-').concat(' ').concat(translatedType),
      latest: {
        id,
        reportType,
        score: {
          date,
          formattedDate,
          value: score,
          percentage,
          reasons,
          ranges,
          ranking,
          loan: {
            risk: {
              ranges: loanRiskRanges,
            },
          },
        },
        summary: {
          security: {
            freeze: creditFileSecurityFreezeFlag,
          },
          accounts: {
            average: {
              age: averageAccountAgeMonths,
            },
            revolving: {
              available: {
                amount: revolvingAvailableAmt,
              },
              balance: {
                amount: revolvingBalanceAmt,
                currency: revolvingBalanceCurrency,
              },
              limit: {
                amount: revolvingCreditLimitAmt,
              },
              ratio: revolvingDtcRatio,
              payment: {
                amount: revolvingMonthlyPaymentAmount,
              },
              total: {
                count: revolvingTotal,
                balanced: revolvingTotalWithBalance,
              },
            },
          },
        },
        providers: providerViewFinal,
      },
    };

    return report;
  } else {
    logObj.message = `reportAndInsights is not an array=>${typeof reportAndInsights}`;
    utils.log(logObj);
    return null;
  }
}

// Helper functions for data conversion

function convertedSummaryFromReport(
  item: {
    report: UnifiedCreditReportModel;
    insights: UnifiedCreditInsightsModel;
    utilizationSummary: CreditReportSummaryAccounts;
  },
  countryCode: string
): ProviderViewFinalType['summary'] {
  const {report, insights, utilizationSummary} = item;
  return {
    id: report.reportId || '',
    reportGenerated: safeNumberConvert(report.generatedDate),
    creditFileSecurityFreezeFlag: report.creditFreezeStatus?.isFrozen || false,
    reportType: report.reportType,
    provider: report.provider,
    subject: createSubjectFromPersonalInfo(report.personalInformation, report.provider),
    creditScore: {} as ProviderViewFinalType['summary']['creditScore'], // This will be filled in the main function
    revolvingAccounts: convertAccountSummary(
      countryCode,
      utilizationSummary.revolvingAccounts,
      report.accounts.revolving
    ),
    mortgageAccounts: convertAccountSummary(countryCode, utilizationSummary.mortgageAccounts, report.accounts.mortgage),
    installmentAccounts: convertAccountSummary(
      countryCode,
      utilizationSummary.instalmentAccounts,
      report.accounts.instalment
    ),
    otherAccounts: convertAccountSummary(countryCode, utilizationSummary.otherAccounts, report.accounts.otherAccounts),
    totalOpenAccounts: convertAccountSummary(
      countryCode,
      utilizationSummary.totalOpenAccounts,
      getAllAccounts(report.accounts)
    ),
    lengthOfCreditHistoryMonths: 0,
    totalNegativeAccounts: 0,
    averageAccountAgeMonths: insights?.creditAge?.currentCreditAge?.totalAgeInMonths || 0,
    oldestAccountOpenDate: 0,
    oldestAccountName: '',
    mostRecentAccountOpenDate: 0,
    mostRecentAccountName: '',
    totalConsumerStatements: 0,
    mostRecentInquiryDate: 0,
    mostRecentInquiryName: '',
    totalPersonalInformation: countPersonalInformation(report.personalInformation),
    totalInquiries: report.inquiries?.length || 0,
    totalPublicRecords: report.publicRecords?.length || 0,
    totalCollections: report.collections?.length || 0,
    disputeInformation: null,
  };
}

function createSubjectFromPersonalInfo(personalInfo: PersonalInformation, provider: string): Subject {
  return {
    provider,
    currentName: personalInfo.currentName,
    aliases: personalInfo.aliases || null,
    currentAddress: convertToAddress1(personalInfo.addresses?.[0]),
    previousAddresses: personalInfo.addresses?.slice(1).map(convertToAddress1) || null,
    homePhone: null,
    mobilePhone: null,
    nationalIdentifier: '',
    dateOfBirth: safeNumberConvert(personalInfo.dateOfBirth),
    dateOfDeath: null,
    employmentHistory: null,
  };
}

function convertToAddress1(address?: InternationalAddress): Address1 | null {
  if (!address) return null;

  return {
    country: {
      code: address.country || '',
      name: address.country || null,
    },
    line1: address.address1 || '',
    line2: address.address2 || null,
    line3: address.city || '',
    line4: address.state || '',
    line5: address.zip || '',
    firstReportedDate: safeNumberConvert(address.dates?.firstReported),
    lastReportedDate: safeNumberConvert(address.dates?.lastUpdated),
    phone: null,
  };
}

function convertAccountSummary(
  country: string,
  accountSummary?: AccountSummary,
  accounts?: CreditReportAccount[]
): AccountSummary1 | null {
  if (!accountSummary) return null;

  const totalAccounts = accounts?.length || 0;
  const totalNegativeAccounts = 0;
  const totalAccountsWithBalance = accounts?.filter((acc) => (acc.amounts?.balanceAmount?.amount || 0) > 0).length || 0;

  const totalMonthlyPayment =
    accounts?.reduce((sum, acc) => {
      return sum + (acc.amounts?.monthlyPayment?.amount || 0);
    }, 0) || 0;

  return {
    balance: {
      amount: accountSummary.balanceAmount?.amount || 0,
      currency: getCurrency(accountSummary.balanceAmount?.currency, country),
    },
    creditLimit: {
      amount: accountSummary.creditLimit?.amount || 0,
      currency: getCurrency(accountSummary.creditLimit?.currency, country),
    },
    available: {
      amount: accountSummary.availableAmount?.amount || 0,
      currency: getCurrency(accountSummary.availableAmount?.currency, country),
    },
    monthlyPaymentAmount: {
      amount: totalMonthlyPayment,
      currency: getCurrency('', country),
    },
    debtToCreditRatio: accountSummary.debtToCreditRatio || 0,
    totalAccounts,
    totalNegativeAccounts,
    totalAccountsWithBalance,
  };
}

function convertToCreditAccounts(
  country: string,
  accounts?: CreditReportAccount[],
  provider?: string
): CreditAccount[] | null {
  if (!accounts || accounts.length === 0) return null;

  return accounts.map((account) => ({
    provider: provider || '',
    id: account.accountNumber || '',
    tradeIdentifier: null,
    accountOpen: account.isAccountOpen || false,
    accountName: account.accountName || '',
    psoName: null,
    contactInformation: {
      contactName: account.contactInformation?.name || '',
      address: convertToAddress1(account.contactInformation?.address),
      phone: account.contactInformation?.phone || null,
    },
    accountNumber: account.accountNumber || '',
    accountStatus: account.accountStatus?.code || '',
    paymentResponsibility: 'INDIVIDUAL',
    highCreditAmount: {
      amount: account.amounts?.highCreditAmount?.amount || 0,
      currency: getCurrency(account.amounts?.highCreditAmount?.currency, country),
    },
    accountType: account.accountType?.code as 'REVOLVING' | 'INSTALLMENT' | 'OTHER',
    creditLimitAmount: {
      amount: account.amounts?.creditLimitAmount?.amount || 0,
      currency: getCurrency(account.amounts?.creditLimitAmount?.currency, country),
    },
    termDurationMonths: 0,
    termFrequency: null,
    dateOpened: safeNumberConvert(account.dates?.dateOpened),
    balanceAmount: {
      amount: account.amounts?.balanceAmount?.amount || 0,
      currency: getCurrency(account.amounts?.balanceAmount?.currency, country),
    },
    reportedDate: safeNumberConvert(account.dates?.reportedDate),
    pastDueAmount: {
      amount: account.amounts?.pastDueAmount?.amount || 0,
      currency: getCurrency(account.amounts?.pastDueAmount?.currency, country),
    },
    lastPaymentDate: safeNumberConvert(account.dates?.lastPaymentDate),
    actualPayment: null,
    monthlyPayment: {
      amount: account.amounts?.monthlyPayment?.amount || 0,
      currency: getCurrency(account.amounts?.monthlyPayment?.currency, country),
    },
    lastActivityDate: safeNumberConvert(account.dates?.lastActivityDate),
    majorDelinquencyFirstReportedDate: null,
    monthsReviewed: 0,
    creditorClassification: account.loanType || '',
    activityDesignator: account.isAccountOpen ? 'OPEN' : 'CLOSED',
    chargeOffAmount: null,
    deferredPaymentStartDate: null,
    balloonPaymentAmount: null,
    balloonPaymentDate: null,
    dateClosed: null,
    loanType: {
      code: account.loanType || '',
      description: account.loanType || '',
    },
    firstDelinquencyDate: null,
    comments: null,
    paymentHistory: convertPaymentHistory(account.paymentHistory),
    trendedDataHistory: {
      name: null,
      trendedDataList: null,
    },
    isNegative: false,
    accountCondition: account.standing?.code || '',
    accountTypeDescription: account.accountType?.description || '',
  }));
}

function convertPaymentHistory(paymentHistory?: {[year: string]: MonthlyPayment[]}): PaymentHistory {
  if (!paymentHistory) return null;

  const years = Object.keys(paymentHistory).sort();
  if (years.length === 0) return null;

  return years.map((yearStr) => {
    const year = parseInt(yearStr, 10);
    const yearlyHistory: YearlyPaymentHistory = {year};

    const monthsData = paymentHistory[yearStr];
    monthsData.forEach((monthData) => {
      const monthName = monthData.month.toLowerCase() as Exclude<keyof YearlyPaymentHistory, 'year'>;
      if (
        [
          'january',
          'february',
          'march',
          'april',
          'may',
          'june',
          'july',
          'august',
          'september',
          'october',
          'november',
          'december',
        ].includes(monthName)
      ) {
        yearlyHistory[monthName] = {
          monthType: monthData.monthType as 'POSITIVE' | 'NEGATIVE' | 'NO_DATA',
          value: monthData.value,
        };
      }
    });

    return yearlyHistory;
  });
}

function convertToPublicRecords1(publicRecords?: PublicRecords[], provider?: string): PublicRecords1 | null {
  if (!publicRecords || publicRecords.length === 0) return null;

  const result: PublicRecords1 = {
    provider: provider || '',
    bankruptcies: [],
    judgments: [],
    liens: [],
  };

  publicRecords.forEach((record) => {
    const recordType = record.type.toLowerCase();

    if (recordType.includes('bankruptcy')) {
      result.bankruptcies.push({
        id: record.caseNumber || '',
        filedDate: safeNumberConvert(record.filedDate),
        reportedDate: safeNumberConvert(record.reportedDate),
        referenceNumber: record.caseNumber || '',
        dispositionStatus: {
          code: '',
          description: '',
        },
        courtName: record.courtName || '',
        liability: null,
        exemptAmount: null,
        assetAmount: null,
      });
    } else if (recordType.includes('judgment')) {
      result.judgments.push({
        id: record.caseNumber || '',
        amount: {amount: 0, currency: '$'},
        filedDate: safeNumberConvert(record.filedDate),
      });
    } else if (recordType.includes('lien')) {
      result.liens.push({
        id: record.caseNumber || '',
        amount: {amount: 0, currency: '$'},
        filedDate: safeNumberConvert(record.filedDate),
      });
    }
  });

  return result.bankruptcies.length || result.judgments.length || result.liens.length ? result : null;
}

function convertToCollections(country: string, collections?: CollectionItem[], provider?: string): Collection[] | null {
  if (!collections || collections.length === 0) return null;

  return collections.map((collection) => ({
    provider: provider || '',
    id: null,
    reportedDate: safeNumberConvert(collection.dates?.reportedDate),
    assignedDate: safeNumberConvert(collection.dates?.assignedDate),
    agencyClient: null,
    agencyContactInformation: {
      contactName: collection.agency || '',
      address: null,
      phone: null,
    },
    originalCreditorName: '',
    orginalAmountOwed: {
      amount: collection.amounts?.originalAmountOwed?.amount || 0,
      currency: getCurrency(collection.amounts?.originalAmountOwed?.currency, country),
    },
    accountNumber: collection.accountNumber || null,
    accountDesignatorCode: '',
    balanceDate: safeNumberConvert(collection.dates?.balanceDate),
    statusDate: safeNumberConvert(collection.dates?.statusDate),
    status: collection.status || 'UNPAID',
    amount: {
      amount: collection.amounts?.amount?.amount || 0,
      currency: getCurrency(collection.amounts?.amount?.currency, country),
    },
  }));
}

function getAllAccounts(accounts: CreditReportAccounts): CreditReportAccount[] {
  return [
    ...(accounts.revolving || []),
    ...(accounts.mortgage || []),
    ...(accounts.instalment || []),
    ...(accounts.otherAccounts || []),
    ...(accounts.bankingRecords || []),
  ];
}

function countPersonalInformation(personalInfo?: PersonalInformation): number {
  if (!personalInfo) return 0;

  let count = 0;
  if (personalInfo.currentName) count++;
  if (personalInfo.aliases?.length) count += personalInfo.aliases.length;
  if (personalInfo.dateOfBirth) count++;
  if (personalInfo.addresses?.length) count += personalInfo.addresses.length;
  return count;
}

/**
 * Returns a valid currency code or country specific countrycode if the input is 'UNKNOWN' or empty string.
 * @param currency - {string} The currency code to validate.
 */
export function getCurrency(currency: string, country = 'US'): string {
  return !currency || currency === 'UNKNOWN' ? currencyCodes[country as keyof typeof currencyCodes] : currency;
}

/**
 * Safely converts a value to a number, returning a fallback value if conversion fails.
 * @param value - {string} The value to convert to a number.
 * @param fallback - {number} The fallback value to return if conversion fails (default is 0).
 */
export function safeNumberConvert(value: string, fallback = 0): number {
  const converted = Number(value);
  return Number.isNaN(converted) ? fallback : converted;
}
